import { Enum_Review_Reviewtype, Review } from '@/types';

/**
 * Drop repeat reviews by author + body. CMS content editors have previously
 * created duplicate entries for the same review — used anywhere reviews are
 * rendered or turned into structured data so a data-entry mistake can't show
 * (or mark up) the same review more than once.
 */
export function dedupeReviews(reviews: Review[]): Review[] {
  const seen = new Set<string>();
  return reviews.filter((r) => {
    const key = `${r.Author}::${r.ReviewText}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Reviews are synced in wholesale from Reviews.io (see the CMS's
 * syncAllReviews) — this is the display policy on top of that full archive:
 * how many show, and which ones. */
const MAX_VISIBLE_REVIEWS = 5;

/**
 * The exact set of reviews that render in the homepage's TestimonialsSection
 * carousel. Structured data must mark up reviews that are genuinely visible
 * on the page — so this is the single source of truth both the visible
 * carousel and the SoftwareApplication review[] markup are built from, never
 * two independently-maintained filters that could drift apart.
 *
 * All-or-nothing: the carousel/markup always shows exactly MAX_VISIBLE_REVIEWS
 * reviews. Fewer than that isn't enough to be worth showing, so an incomplete
 * set is dropped entirely rather than shown partial.
 */
export function getVisibleCustomerReviews(reviews: Review[]): Review[] {
  const fiveStar = dedupeReviews(
    reviews.filter(
      (review) =>
        review?.Active === true &&
        !!review?.ReviewText &&
        review?.ReviewType === Enum_Review_Reviewtype.Customer &&
        review?.Rating === 5 &&
        !!review?.ReviewDate
    )
  );

  if (fiveStar.length < MAX_VISIBLE_REVIEWS) return [];

  return fiveStar
    .slice()
    .sort((a, b) => b.ReviewDate!.localeCompare(a.ReviewDate!))
    .slice(0, MAX_VISIBLE_REVIEWS);
}

/** Same 1-5 clamp TestimonialsSection uses to display stars — reused so the
 * markup's reviewRating always matches what's shown on the page. */
export function normaliseRating(rating: Review['Rating']): number {
  const value = Number(rating ?? 5);
  if (Number.isNaN(value)) return 5;
  return Math.max(0, Math.min(5, value));
}
